import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../lib/firebase';
import { sendEmailVerification, reload } from 'firebase/auth';
import { motion, AnimatePresence } from 'motion/react';
import { Mail, RefreshCw, ArrowRight, CheckCircle2, LogOut } from 'lucide-react';

const VerifyEmail: React.FC = () => {
  const [user, setUser] = useState(auth.currentUser);
  const [cooldown, setCooldown] = useState(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is already verified on mount
    if (!auth.currentUser) {
      navigate('/');
      return;
    }
    
    if (auth.currentUser.emailVerified) {
      handleRedirection();
    }

    const interval = setInterval(async () => {
      if (auth.currentUser) {
        await reload(auth.currentUser);
        if (auth.currentUser.emailVerified) {
          handleRedirection();
        }
      }
    }, 3000); // Poll every 3s as a backup

    return () => clearInterval(interval);
  }, [navigate]);

  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  const handleRedirection = () => {
    // Inscription promoteur : le rôle 'festival' est forcé côté AuthModal pour la
    // structure du compte (nécessaire), mais l'intention réelle n'est pas de créer
    // un festival — sans ce marqueur, l'utilisateur atterrissait dans ce tunnel.
    if (localStorage.getItem('festin_pending_promoter_signup') === '1') {
      localStorage.removeItem('festin_pending_promoter_signup');
      navigate('/become-promoter');
      return;
    }

    // Determine where to go based on user metadata or stored preference
    // For now, redirect to a generic onboarding or home
    const userRole = localStorage.getItem('userRole'); // Assuming we store this during signup
    if (userRole === 'festival') {
      navigate('/choose-creation-method');
    } else if (userRole === 'stand') {
      navigate('/my-stand');
    } else {
      navigate('/home');
    }
  };

  const handleResend = async () => {
    if (cooldown > 0 || !auth.currentUser) return;
    
    setLoading(true);
    setMessage(null);
    try {
      // Mail brandé Festin via le backend (Brevo). Repli sur le mail Firebase
      // par défaut si le backend est indisponible — mieux qu'aucun envoi.
      const token = await auth.currentUser.getIdToken();
      const role = localStorage.getItem('userRole') || '';
      const resp = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ role }),
      });
      if (!resp.ok) throw new Error('resend failed');
      setMessage({ text: "Email de vérification renvoyé !", type: 'success' });
      setCooldown(60);
    } catch (error: any) {
      try {
        await sendEmailVerification(auth.currentUser);
        setMessage({ text: "Email de vérification renvoyé !", type: 'success' });
        setCooldown(60);
      } catch {
        setMessage({ text: "Erreur lors de l'envoi. Réessayez plus tard.", type: 'error' });
        console.error(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const checkVerification = async () => {
    setLoading(true);
    try {
      if (auth.currentUser) {
        await reload(auth.currentUser);
        if (auth.currentUser.emailVerified) {
          handleRedirection();
        } else {
          setMessage({ text: "L'email n'est pas encore vérifié.", type: 'error' });
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await auth.signOut();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col items-center justify-center p-6 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#ff5a00]/10 rounded-full blur-[120px] pointer-events-none"></div>
      
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-white/5 backdrop-blur-2xl border border-white/10 rounded-[32px] p-8 md:p-10 relative z-10 shadow-2xl"
      >
        <div className="flex flex-col items-center text-center">
          <div className="w-20 h-20 bg-[#ff5a00]/20 rounded-3xl flex items-center justify-center mb-8 ring-1 ring-[#ff5a00]/30 shadow-[0_0_30px_rgba(255,90,0,0.15)]">
            <Mail className="w-10 h-10 text-[#ff5a00]" />
          </div>

          <h1 className="text-3xl font-black mb-4 tracking-tight">VÉRIFIEZ VOTRE EMAIL</h1>
          <p className="text-gray-400 mb-8 leading-relaxed">
            Un email de vérification vous a été envoyé à <span className="text-white font-bold">{auth.currentUser?.email}</span>. 
            Veuillez vérifier votre boîte de réception (et vos spams) pour continuer.
          </p>

          <AnimatePresence mode="wait">
            {message && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className={`w-full p-4 rounded-2xl mb-6 flex items-center gap-3 overflow-hidden ${
                  message.type === 'success' ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'
                }`}
              >
                {message.type === 'success' ? <CheckCircle2 className="w-5 h-5 flex-shrink-0" /> : <div className="w-5 h-5 rounded-full border border-red-400 flex items-center justify-center text-xs font-bold">!</div>}
                <span className="text-sm font-bold">{message.text}</span>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="w-full space-y-4">
            <button
              onClick={checkVerification}
              disabled={loading}
              className="w-full bg-white text-black font-black py-4 rounded-2xl flex items-center justify-center group hover:bg-[#ff5a00] hover:text-white transition-all active:scale-95 disabled:opacity-50"
            >
              {loading ? <RefreshCw className="w-6 h-6 animate-spin" /> : (
                <>
                  J'AI VÉRIFIÉ MON EMAIL
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>

            <button
              onClick={handleResend}
              disabled={cooldown > 0 || loading}
              className="w-full bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold py-4 rounded-2xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <RefreshCw className="w-5 h-5" />}
              {cooldown > 0 ? `RENVOYER DANS ${cooldown}S` : "RENVOYER L'EMAIL"}
            </button>
          </div>

          <button 
            onClick={handleSignOut}
            className="mt-8 text-gray-500 hover:text-white text-sm font-bold flex items-center gap-2 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            SE DÉCONNECTER
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default VerifyEmail;
